import {
  type ErrorEvent,
  extraErrorDataIntegration,
  type Integration,
  type Options,
  type StackFrame,
} from "@sentry/core";
import { isProductionEnvironment } from "@remi/environment";
import { DbErrorType, QueryTimeoutCode, RemiServiceError } from "@remi/error";

import { reportToSentryMetricCounter } from "./error-reporter/capture";
import { addPostHogContextToSentryEvent } from "./integrations/posthog";
import { scrubCreditBreadcrumb, scrubPiiFromEvent } from "./pii-scrubber";
import { getSentryEnvironment } from "./utils";

const MARKETING_LEAD_INTAKE_PATH = "/api/v0/marketing/leads";

const isMarketingLeadIntakeRequest = (event: { request?: { url?: string }; transaction?: string }): boolean =>
  [event.request?.url, event.transaction].some(
    (value) => typeof value === "string" && value.includes(MARKETING_LEAD_INTAKE_PATH),
  );

function isNotEditableChecklistError(value: unknown): boolean {
  if (value instanceof RemiServiceError) {
    return value.errorType === "CHECKLIST_SUBMISSION_NOT_EDITABLE";
  }
  if (value instanceof Error && value.cause instanceof RemiServiceError) {
    return value.cause.errorType === "CHECKLIST_SUBMISSION_NOT_EDITABLE";
  }
  return false;
}

const ABORTED_EXCEPTION_VALUE = "Aborted";
const HONO_ABORT_LISTENER_FRAME = "AbortSignal.listener";
// A Chromium extension injects `executors/<n>.js` (never served by us) and throws `reading 'M_ID'` on
// every XHR it intercepts; nextjs frame normalization rewrites its chrome-extension:// origin to app:///.
const BROWSER_EXTENSION_EXECUTOR_URL = /\/executors\/\d+\.js$/;
const IGNORED_LOW_VALUE_SPANS: NonNullable<Options["ignoreSpans"]> = [
  { name: /^middleware (GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)$/, op: "http" },
];

export function isClientDisconnectAbort(exceptionValue: string | undefined, frames: StackFrame[] | undefined): boolean {
  if (exceptionValue !== ABORTED_EXCEPTION_VALUE) {
    return false;
  }
  // Require @hono/node-server's request-abort listener on the stack so a genuine app-thrown "Aborted" is kept.
  return (frames ?? []).some((frame) => frame.function === HONO_ABORT_LISTENER_FRAME);
}

// Cross-route contract: the server-side Sentry Fingerprint Rule covering raw Prisma cut-over errors
// sets this same value. Rules overwrite client fingerprints unconditionally, so if the two drift the
// populations split into two issues and nothing in CI can see it.
export const VTTABLET_CUTOVER_FINGERPRINT = ["db", "vttablet-cutover"] as const;

const PRISMA_REQUEST_ERROR_NAMES = new Set(["PrismaClientKnownRequestError", "PrismaClientUnknownRequestError"]);
const UNKNOWN_QUERY_NAME = "unknown";

const COLLAPSED_TIMEOUT_CODE = QueryTimeoutCode.TerminateAll;

// ExtraErrorData defaults to depth 3, collapsing nested error `data` (e.g. wrapped Sunlight `errors[]`)
// to "[Object]" in Sentry. This must match normalizeDepth so wrapped-error payloads stay legible.
export const EXTRA_ERROR_DATA_DEPTH = 10;

export const VTTABLET_CUTOVER_SHAPES = [
  { code: QueryTimeoutCode.TerminateAll, messageToken: "QueryList.TerminateAll" },
  { code: QueryTimeoutCode["1105"], messageToken: "buffered for cut-over" },
] as const satisfies readonly { code: QueryTimeoutCode; messageToken: string }[];

// The server-side Sentry Fingerprint Rule covering transient connection loss must set this identical
// value, or the populations split into two issues and nothing in CI can see it.
export const TRANSIENT_DB_CONNECTION_FINGERPRINT = ["db", "transient-connection"] as const;

export const TRANSIENT_DB_CONNECTION_CODES: readonly string[] = ["P1017"];

function readCutoverData(source: unknown): { timeoutCode: string | undefined; query: string | undefined } {
  const empty = { timeoutCode: undefined, query: undefined };
  if (source === null || typeof source !== "object" || !("data" in source)) {
    return empty;
  }
  const { data } = source;
  if (data === null || typeof data !== "object") {
    return empty;
  }
  const timeoutCode: unknown = "timeoutCode" in data ? data.timeoutCode : undefined;
  const query: unknown = "query" in data ? data.query : undefined;
  return {
    timeoutCode: typeof timeoutCode === "string" ? timeoutCode : undefined,
    query: typeof query === "string" ? query : undefined,
  };
}

function isPrismaRequestError(value: unknown): value is Error {
  return value instanceof Error && PRISMA_REQUEST_ERROR_NAMES.has(value.name) && "clientVersion" in value;
}

interface QueryTimeoutErrorShape {
  errorType: typeof DbErrorType.QUERY_TIMEOUT;
  data?: unknown;
}

function isQueryTimeoutRemiError(value: unknown): value is QueryTimeoutErrorShape {
  return (
    value !== null && typeof value === "object" && "errorType" in value && value.errorType === DbErrorType.QUERY_TIMEOUT
  );
}

function findCause<T>(value: unknown, matches: (candidate: unknown) => candidate is T): T | undefined {
  if (matches(value)) {
    return value;
  }
  if (value instanceof Error && matches(value.cause)) {
    return value.cause;
  }
  return undefined;
}

function resolveCutover(
  event: ErrorEvent,
  originalException: unknown,
): { code: QueryTimeoutCode | undefined; queryName: string } {
  const fromEvent = readCutoverData(event.extra);
  const queryTimeoutError = findCause(originalException, isQueryTimeoutRemiError);
  if (queryTimeoutError) {
    const fromError = readCutoverData(queryTimeoutError);
    const timeoutCode = fromError.timeoutCode ?? fromEvent.timeoutCode;
    return {
      code: timeoutCode === COLLAPSED_TIMEOUT_CODE ? COLLAPSED_TIMEOUT_CODE : undefined,
      queryName: fromError.query ?? fromEvent.query ?? UNKNOWN_QUERY_NAME,
    };
  }
  const prismaError = findCause(originalException, isPrismaRequestError);
  const code = prismaError
    ? VTTABLET_CUTOVER_SHAPES.find((shape) => prismaError.message.includes(shape.messageToken))?.code
    : undefined;
  return { code, queryName: UNKNOWN_QUERY_NAME };
}

export function matchVttabletCutoverEvent(event: ErrorEvent, originalException: unknown): QueryTimeoutCode | undefined {
  return resolveCutover(event, originalException).code;
}

function resolveTransientDbConnectionCode(originalException: unknown): string | undefined {
  const prismaError = findCause(originalException, isPrismaRequestError);
  if (!prismaError || !("code" in prismaError)) {
    return undefined;
  }
  const { code } = prismaError;
  return typeof code === "string" && TRANSIENT_DB_CONNECTION_CODES.includes(code) ? code : undefined;
}

/**
 * Get base Sentry configuration that's shared across all environments
 */
export function getBaseSentryConfig(additionalIntegrations?: Integration[]): Options {
  const environment = getSentryEnvironment();
  return {
    environment,
    // Prod must omit the key: a present-but-undefined `release` overrides the release the Next.js
    // build injects into browser bundles. Non-prod pins the env name so @sentry/node-core skips
    // its VERCEL_GIT_COMMIT_SHA auto-detect.
    ...(isProductionEnvironment() ? {} : { release: environment }),
    sendDefaultPii: true,
    denyUrls: [/posthog-recorder\.js/, BROWSER_EXTENSION_EXECUTOR_URL],
    ignoreSpans: IGNORED_LOW_VALUE_SPANS,
    _experiments: {
      enableMetrics: true,
    },
    integrations: (defaultIntegrations: Integration[]) => {
      if (additionalIntegrations) {
        defaultIntegrations.push(...additionalIntegrations);
      }
      const filteredIntegrations = defaultIntegrations.filter(
        (integration) => integration.name !== "Prisma" && integration.name !== "ExtraErrorData",
      );
      return [...filteredIntegrations, extraErrorDataIntegration({ depth: EXTRA_ERROR_DATA_DEPTH })];
    },
    beforeBreadcrumb: (breadcrumb) => {
      // Drop all HTTP breadcrumbs in non-browser runtimes (Node.js server + edge) — they leak
      // across concurrent requests due to shared scope in serverless environments.
      // OTEL traces capture HTTP activity accurately in all server runtimes.
      if (typeof window === "undefined" && breadcrumb.type === "http") {
        return null;
      }

      // Client-side: exclude fetch requests to internal services that create noise
      if (breadcrumb.type === "http" && breadcrumb.data?.url) {
        const url = String(breadcrumb.data.url);
        if (url.includes("posthog") || url.includes("axiom") || url.includes("clerk") || url.includes("ably")) {
          return null;
        }
      }

      // Strip PII from credit endpoint breadcrumbs (client-side only — server HTTP breadcrumbs are dropped above)
      scrubCreditBreadcrumb(breadcrumb);

      return breadcrumb;
    },
    beforeSend: (event, hint) => {
      // Scrub PII from request data (SSN, DOB, income)
      scrubPiiFromEvent(event);

      // sendDefaultPii is on, so a 500 on the lead intake route would otherwise ship the form body (name, phone,
      // email, address, click ids) to Sentry.
      if (event.request !== undefined && isMarketingLeadIntakeRequest(event)) {
        const { data: _data, query_string: _queryString, cookies: _cookies, ...request } = event.request;
        event.request = request;
      }

      const exception = hint.originalException;

      const errorType =
        exception && typeof exception === "object" && "errorType" in exception ? exception.errorType : undefined;

      const imposterId = event.user?.imposter_id;
      if (errorType === "UNAUTHORIZED" && imposterId) {
        return null;
      }

      if (isNotEditableChecklistError(exception)) {
        return null;
      }

      if (typeof window === "undefined") {
        // @hono/node-server aborts the in-flight request when the client socket closes mid-request (e.g.
        // QStash dropping a long oddjob-api workflow); Node rejects it with "Aborted" — not an app fault.
        const firstException = event.exception?.values?.[0];
        if (isClientDisconnectAbort(firstException?.value, firstException?.stacktrace?.frames)) {
          reportToSentryMetricCounter("server.request.client-disconnect-abort", 1, {
            attributes: { route: event.transaction ?? "unknown" },
          });
          return null;
        }

        const { code: cutoverCode, queryName } = resolveCutover(event, exception);
        if (cutoverCode) {
          event.fingerprint = [...VTTABLET_CUTOVER_FINGERPRINT];
          event.tags = { ...event.tags, timeout_code: cutoverCode, query_name: queryName };
          reportToSentryMetricCounter("db.vttablet-cutover.error-collapsed", 1, {
            attributes: { query: queryName, timeoutCode: cutoverCode },
          });
        } else {
          const transientCode = resolveTransientDbConnectionCode(exception);
          if (transientCode) {
            event.fingerprint = [...TRANSIENT_DB_CONNECTION_FINGERPRINT];
            event.tags = { ...event.tags, prisma_code: transientCode };
            reportToSentryMetricCounter("db.transient-connection.error-collapsed", 1, {
              attributes: { prismaCode: transientCode, route: event.transaction ?? "unknown" },
            });
          }
        }
      }

      if (typeof window !== "undefined") {
        const firstException = event.exception?.values?.[0];
        const exceptionValue = firstException?.value;

        if (exceptionValue === "Worker was terminated") {
          return null;
        }

        // WebKit engine constant, rejected into the page by third-party iOS in-app browsers, never by our code.
        if (exceptionValue === "WKWebView API client did not respond to this postMessage") {
          reportToSentryMetricCounter("browser.webview-bridge.unresponsive", 1, {
            attributes: { route: event.transaction ?? "unknown" },
          });
          return null;
        }

        if (firstException?.type === "TypeError" && exceptionValue?.includes("sendWithPromise")) {
          return null;
        }

        if (firstException?.type === "AbortError" || exceptionValue?.startsWith("AbortError:")) {
          const route = event.transaction ?? "unknown";
          const failedFetch = event.breadcrumbs?.filter((b) => b.type === "http" && b.level === "error").pop();
          const requestPath = failedFetch?.data?.url ?? "unknown";
          reportToSentryMetricCounter("profile.route.abort", 1, {
            attributes: { route, requestPath },
          });
          return null;
        }

        if (exceptionValue?.startsWith("Load failed") || exceptionValue?.startsWith("Failed to fetch")) {
          const route = event.transaction ?? "unknown";

          // Find the last failed fetch request from breadcrumbs
          const failedFetch = event.breadcrumbs?.filter((b) => b.type === "http" && b.level === "error").pop();
          const requestPath = failedFetch?.data?.url ?? "unknown";

          const metric = exceptionValue.startsWith("Load failed")
            ? "trpc.client.ios-load-failed"
            : "trpc.client.failed-to-fetch";
          reportToSentryMetricCounter(metric, 1, {
            attributes: { route, requestPath },
          });
          return null;
        }
      }

      // Only add PostHog context on client-side (browser environment)
      if (typeof window !== "undefined") {
        return addPostHogContextToSentryEvent(event, hint);
      }
      return event;
    },
    normalizeDepth: EXTRA_ERROR_DATA_DEPTH,
  };
}
